import { api } from "@/core/api";
import type { ClubInfoView, ClubLifecycleStatus } from "@/club/types/club";
import type { DistrictEntry } from "@/club/types/club";
import { DEFAULT_CLUB_MEETING_CONFIG } from "@/club/types/club";
import type { PeriodicEmailData } from "@/types/periodic-email";
import { apolloClient } from "@/graphql/client";
import { runGraphQL } from "@/graphql/runGraphQL";
import { VERSIONED_READ_POLICY, versionedReadPolicyWithContext } from "@/graphql/versionedReadPolicy";
import {
  GetClubIdByShortNameDocument,
  GetClubInfoDocument,
  CheckShortNameAvailabilityDocument,
  GetClubHierarchyDocument,
  GetPeriodicEmailPreviewDocument,
  DeleteClubImageDocument,
  UpdateEmailConfigurationDocument,
  TestEmailConfigurationDocument,
  TriggerPeriodicEmailDocument,
  UpdateNotificationPreferencesDocument,
  GetEasySpeakInstancesDocument,
  type EmailConfigInput,
  type GetClubHierarchyQuery,
  type GetEasySpeakInstancesQuery,
} from "@/graphql/generated/types";
import type { EasySpeakInstanceOption } from "@/graphql/generated/base-types";

type ClubHierarchyGqlDistrict = GetClubHierarchyQuery["clubHierarchy"][number];
import type { ClubNotificationPreferences } from "@/club/types/club";

import { ClubId } from "@/dashboard";

// Cache for club short name to club ID mapping
const clubIdCache: Record<string, ClubId> = {};

export const clubService = {
  /**
   * Get club ID by short name with aggressive caching
   * @param shortName The club's short name
   * @returns Promise with the club ID as a ClubId object
   */
  async getClubIdByShortName(shortName: string): Promise<ClubId> {
    // Check cache first
    if (clubIdCache[shortName] !== undefined) {
      return clubIdCache[shortName];
    }

    const data = runGraphQL(
      await apolloClient.query({
        query: GetClubIdByShortNameDocument,
        variables: { shortName },
        fetchPolicy: "network-only",
      }),
      { fallbackMessage: "Failed to look up the club." },
    );

    const rawId = data.clubIdByShortName;
    if (rawId == null) {
      throw new Error(`Club not found for short name: ${shortName}`);
    }

    const clubId: ClubId = { v: rawId };
    clubIdCache[shortName] = clubId;
    return clubId;
  },

  /**
   * Send a periodic email for the club
   * @param clubShortName The short name of the club
   * @returns Promise with success status and message
   */
  async sendPeriodicEmail(clubId: number): Promise<{ success: boolean; message: string }> {
    const data = runGraphQL(
      await apolloClient.mutate({
        mutation: TriggerPeriodicEmailDocument,
        variables: { clubId },
      }),
      { fallbackMessage: "Failed to send the periodic email." },
    );

    return {
      success: data.triggerPeriodicEmail?.success ?? false,
      message: data.triggerPeriodicEmail?.message ?? "",
    };
  },

  /**
   * Preview the periodic email for the club
   * @param clubShortName The short name of the club
   * @returns Promise with email preview data
   */
  async previewPeriodicEmail(clubId: number): Promise<{ subject: string; body: string }> {
    const data = runGraphQL(
      await apolloClient.query({
        query: GetPeriodicEmailPreviewDocument,
        variables: { clubId: String(clubId) },
        fetchPolicy: "network-only",
      }),
      { fallbackMessage: "Failed to load the email preview." },
    );

    return {
      subject: "Club Periodic Email Preview",
      body: data.periodicEmailPreview?.emailHtml ?? "",
    };
  },

  /**
   * Fetch the server-rendered periodic email preview for a club.
   * @param clubShortName The short name of the club
   * @returns Promise with the rendered email preview data
   */
  async getPeriodicEmailPreview(clubShortName: string): Promise<PeriodicEmailData> {
    return api.get<PeriodicEmailData>(`/api/club/${clubShortName}/periodicEmailPreview`, {
      fallbackMessage: "Failed to load email preview.",
    });
  },

  /**
   * Unsubscribe a guest from a club's guest list via their one-time remove id.
   * @param removeId The opaque unsubscribe token from the guest email link
   */
  async unsubscribeGuest(removeId: string): Promise<void> {
    await api.post<void>(`/api/club/guest/unsubscribe/${removeId}`, undefined, {
      fallbackMessage: "Guest not found. You may have already unsubscribed.",
    });
  },

  /**
   * True when Apollo already holds a cached `GetClubInfo` result for this club.
   *
   * Used by the club page to decide whether a background stale-while-revalidate
   * refetch is worthwhile: on a cache miss the initial `cache-first` load already
   * hits the network (so no revalidation is needed), whereas on a cache hit the
   * page paints instantly from cache and should refresh in the background so
   * out-of-band server changes (e.g. a district officer import) become visible
   * without a full reload.
   */
  hasCachedClubInfo(clubShortName: string): boolean {
    try {
      return (
        apolloClient.readQuery({
          query: GetClubInfoDocument,
          variables: { shortName: clubShortName },
        }) != null
      );
    } catch {
      // readQuery throws when the cache holds only partial data for the
      // selection set; treat that as "not usable from cache" so the normal
      // load fetches fresh.
      return false;
    }
  },

  /**
   * Get club data by short name via GraphQL.
   *
   * Defaults to Apollo's normalized cache (`cache-first`). Pass `force: true`
   * after a save/mutation to bypass the cache and re-fetch.
   *
   * A forced read is a *versioned* read and carries `VERSIONED_READ_POLICY`
   * rather than `network-only` ([FEPRIM-VERSIONED-APOLLO-READ]). Every caller of the forced
   * path reloads after a mutation and guards its own write with a request gate,
   * and under deduplication two such reloads share one response — the one the
   * server produced before the mutation — so the gate is handed the same stale
   * payload twice and accepts it. `ClubHome` is where that bites: its background
   * revalidation and an officer handler's reload overlap by design, and the
   * officer watched their change land and then revert (#3141).
   *
   * The cost is that a forced read no longer refreshes the normalized cache, so
   * `hasCachedClubInfo` can still be true while the cached payload predates the
   * last mutation. That is the stale-while-revalidate contract rather than a
   * regression of it: the cached paint was never guaranteed current, and the
   * revalidation it triggers is what corrects it.
   */
  async getClubByShortName(
    clubShortName: string,
    options?: { signal?: AbortSignal; force?: boolean },
  ): Promise<ClubInfoView> {
    const signalContext = options?.signal ? { fetchOptions: { signal: options.signal } } : undefined;
    const data = runGraphQL(
      await apolloClient.query({
        query: GetClubInfoDocument,
        variables: { shortName: clubShortName },
        ...(options?.force
          ? versionedReadPolicyWithContext(signalContext)
          : { fetchPolicy: "cache-first" as const, context: signalContext }),
      }),
      { fallbackMessage: "Failed to load the club." },
    );

    const ci = data.clubInfo;
    if (!ci) {
      throw new Error(`Club not found for identifier: ${clubShortName}`);
    }

    const pub = ci.publicInfo;
    const mgr = ci.managerInfo;

    if (ci.isOnToastHost) {
      clubIdCache[clubShortName] = { v: pub.clubId };
      clubIdCache[pub.shortName] = { v: pub.clubId };
    }

    // Map GraphQL webConfig to frontend ClubWebConfig shape
    const wc = pub.website.webConfig;
    const webConfig = {
      clubId: { v: pub.clubId },
      hostingOption: wc.hostingOption,
      domainName: wc.domainName ?? undefined,
      clubUrl: wc.clubUrl ?? undefined,
      introductionText: wc.introductionText,
      tagLine: wc.tagLine,
      metaDesc: wc.metaDesc ?? undefined,
      logoImage: wc.logoImage ?? undefined,
      backgroundImage: wc.backgroundImage ?? undefined,
      locationShortDesc: wc.locationShortDesc ?? undefined,
      mapLink: wc.mapLink ?? undefined,
      meetingTimes: wc.meetingTimes ?? undefined,
      facebookLink: wc.facebookLink ?? undefined,
      twitterLink: wc.twitterLink ?? undefined,
      instagramLink: wc.instagramLink ?? undefined,
      meetupLink: wc.meetupLink ?? undefined,
      linkedInLink: wc.linkedInLink ?? undefined,
      youtubeLink: wc.youtubeLink ?? undefined,
      eventbriteLink: wc.eventbriteLink ?? undefined,
      websiteImages: {},
    };

    // Map meeting schedule - convert GraphQL array to tuple format
    const meetingSchedule = pub.meetingSchedule
      ? {
          timezone: pub.meetingSchedule.timezone,
          meetingPatterns: pub.meetingSchedule.meetingPatterns.map(
            (p: { weekNumbers: number[]; dayOfWeek: string; startTime: string; durationHours: number }) =>
              [
                p.weekNumbers,
                {
                  dayOfWeek: p.dayOfWeek,
                  startTime: p.startTime,
                  durationHours: p.durationHours,
                },
              ] as [number[], { dayOfWeek: string; startTime: string; durationHours: number }],
          ),
          exclusions: pub.meetingSchedule.exclusions,
        }
      : undefined;

    const meetingConfig = pub.meetingConfig
      ? {
          autoSyncAgendaToVoting: pub.meetingConfig.autoSyncAgendaToVoting,
          autoAddAgendaToFeedback: pub.meetingConfig.autoAddAgendaToFeedback,
          evaluatorVotingRoles: pub.meetingConfig.evaluatorVotingRoles,
          allowMemberRoleSelfAssign: pub.meetingConfig.allowMemberRoleSelfAssign,
          memberSpeechRequestAdvanceDays:
            pub.meetingConfig.memberSpeechRequestAdvanceDays ??
            DEFAULT_CLUB_MEETING_CONFIG.memberSpeechRequestAdvanceDays,
        }
      : undefined;

    // Map calendar
    const mapMeetingSummary = (ms: { id: string; startDate: string; description: string }) => ({
      id: ms.id,
      startDate: ms.startDate,
      description: ms.description,
    });

    const calendar = {
      isEnabled: pub.calendar.isEnabled,
      config: { state: pub.calendar.configState },
      futureMeetings: pub.calendar.futureMeetings.map(mapMeetingSummary),
      nextMeeting: pub.calendar.nextMeeting ? mapMeetingSummary(pub.calendar.nextMeeting) : undefined,
      meetings: pub.calendar.futureMeetings.map(mapMeetingSummary),
    };

    // Map officers
    const mapOfficer = (o: {
      role: string;
      name: string;
      email: string | null;
      termBegin: string;
      termEnd: string;
      userId: string | number | null;
      membershipId: string | null;
    }) => ({
      role: o.role,
      name: o.name,
      email: o.email,
      termBegin: o.termBegin,
      termEnd: o.termEnd,
      userId: o.userId != null ? Number(o.userId) : undefined,
      membershipId: o.membershipId ?? undefined,
    });

    const publicInfo = {
      clubId: pub.clubId,
      shortName: pub.shortName,
      longName: pub.longName,
      contactEmail: pub.contactEmail,
      district: pub.district,
      division: pub.division,
      area: pub.area,
      website: {
        webConfig,
        clubWebsite: pub.website.clubWebsite ?? undefined,
        getClubWebsite: () => pub.website.clubWebsite ?? undefined,
      },
      guestbookConfig: {
        enabled: pub.guestbookConfig.enabled,
        firstEmailBody: pub.guestbookConfig.firstEmailBody,
        reminderEmailBody: pub.guestbookConfig.reminderEmailBody,
      },
      calendar,
      memberCount: pub.memberCount ?? undefined,
      membershipStatus: pub.membershipStatus
        ? {
            enabled: pub.membershipStatus.enabled,
            newMemberRegistrationEnabled: pub.membershipStatus.newMemberRegistrationEnabled,
            cardPaymentsEnabled: pub.membershipStatus.cardPaymentsEnabled,
          }
        : undefined,
      clubOfficers: pub.clubOfficers.map(mapOfficer),
      meetingSchedule,
      meetingConfig,
      tiStatus: pub.tiStatus as ClubLifecycleStatus,
      suspendDate: pub.suspendDate ?? null,
    };

    // Map manager info
    const managerInfo = mgr
      ? {
          easyspeakId: mgr.easyspeakId ?? undefined,
          guestbook: {
            isEnabled: mgr.guestbook.enabled,
            firstEmailBody: mgr.guestbook.firstEmailBody,
            reminderEmailBody: mgr.guestbook.reminderEmailBody,
          },
          periodicEmail: mgr.periodicEmail,
          notificationPreferences: {
            copyInboxOnMeetingRole: mgr.notificationPreferences?.copyInboxOnMeetingRole ?? false,
            copyInboxOnMembership: mgr.notificationPreferences?.copyInboxOnMembership ?? true,
            copyInboxOnGuest: mgr.notificationPreferences?.copyInboxOnGuest ?? true,
          },
          guestCount: mgr.guestCount,
          memberCount: mgr.memberCount,
          membershipStatus: {
            enabled: mgr.membershipStatus.enabled,
            newMemberRegistrationEnabled: mgr.membershipStatus.newMemberRegistrationEnabled,
            cardPaymentsEnabled: mgr.membershipStatus.cardPaymentsEnabled,
          },
          stripeConnected: mgr.stripeConnected,
          clubOfficers: mgr.clubOfficers.map(mapOfficer),
          emailConfiguration: mgr.emailConfiguration
            ? {
                enabled: mgr.emailConfiguration.enabled,
                emailAddress: mgr.emailConfiguration.emailAddress,
                emailPassword: mgr.emailConfiguration.emailPassword,
                imapServer: mgr.emailConfiguration.imapServer,
                smtpServer: mgr.emailConfiguration.smtpServer,
                smtpPort: mgr.emailConfiguration.smtpPort,
              }
            : undefined,
        }
      : undefined;

    // Parse DCP data from JSON string
    const dcpData = ci.dcpData ? JSON.parse(ci.dcpData) : undefined;

    return {
      clubConnection: ci.clubConnection,
      isOnToastHost: ci.isOnToastHost,
      publicInfo,
      managerInfo,
      dcpData,
      clubMeetingsId: ci.clubMeetingsId,
    };
  },

  /**
   * Upload a new image to the club
   * @param clubShortName The short name of the club
   * @param imageFile The image file to upload
   * @returns Promise with upload result
   */
  async uploadClubImage(clubShortName: string, imageFile: File): Promise<{ success: boolean; message?: string }> {
    const formData = new FormData();
    formData.append("imageData", imageFile);

    return api.post<{ success: boolean; message?: string }>(`/api/club/${clubShortName}/images/upload`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      fallbackMessage: "The image could not be uploaded. Please try again.",
    });
  },

  /**
   * Remove an image from the club
   * @param clubShortName The short name of the club
   * @param imageName The name of the image to remove
   * @returns Promise with removal result
   */
  async removeClubImage(
    clubId: number,
    imageName: string,
  ): Promise<{
    success: boolean;
    message?: string;
    wasLogoImage?: boolean;
    wasBackgroundImage?: boolean;
  }> {
    const data = runGraphQL(
      await apolloClient.mutate({
        mutation: DeleteClubImageDocument,
        variables: { clubId, imageName },
      }),
      { fallbackMessage: "Failed to remove the club image." },
    );

    return {
      success: data.deleteClubImage?.success ?? false,
      message: data.deleteClubImage?.message ?? undefined,
      wasLogoImage: data.deleteClubImage?.wasLogoImage ?? undefined,
      wasBackgroundImage: data.deleteClubImage?.wasBackgroundImage ?? undefined,
    };
  },

  /**
   * Check if a club short name is available
   * @param shortName The short name to check
   * @param currentClubId The current club's ID (to exclude from check)
   * @returns Promise with availability status
   */
  async checkShortNameAvailability(
    shortName: string,
    currentClubId?: number,
  ): Promise<{ available: boolean; message?: string }> {
    const data = runGraphQL(
      await apolloClient.query({
        query: CheckShortNameAvailabilityDocument,
        variables: { shortName, currentClubId: currentClubId ?? null },
        fetchPolicy: "network-only",
      }),
      { fallbackMessage: "Failed to check short name availability." },
    );

    return {
      available: data.checkShortNameAvailability?.available ?? false,
      message: data.checkShortNameAvailability?.message ?? undefined,
    };
  },

  /**
   * Get club hierarchy data for Find Club feature
   * @returns Promise with club hierarchy data
   */
  async getClubHierarchy(): Promise<DistrictEntry[]> {
    const data = runGraphQL(
      await apolloClient.query({
        query: GetClubHierarchyDocument,
        // `FindClub` gates this read: the lazy tab-switch load and the retry can
        // overlap, so the two invocations have to be two requests that leave the
        // shared cache alone.
        ...VERSIONED_READ_POLICY,
      }),
      { fallbackMessage: "Failed to load the club directory." },
    );

    // Map GraphQL response to DistrictEntry format. GraphQL returns scalar IDs;
    // wrap them into the display value-objects the hierarchy uses. This service
    // path supplies only the identifier (no display strings — those are
    // optional on the *Display types and populated client-side elsewhere).
    return (data.clubHierarchy ?? []).map((d: ClubHierarchyGqlDistrict) => ({
      districtId: { v: d.districtId },
      districtName: d.districtName,
      divisions: (d.divisions ?? []).map((div) => ({
        divisionId: { c: div.divisionId },
        areas: (div.areas ?? []).map((area) => ({
          areaId: { area: area.areaId },
          clubs: (area.clubs ?? []).map((club) => ({
            clubNumber: club.clubNumber,
            clubShortName: club.clubShortName ?? null,
            clubLongName: club.clubLongName,
            isActive: club.isActive,
            isSuspended: club.isSuspended,
            tiStatus: club.tiStatus as ClubLifecycleStatus,
            suspendDate: club.suspendDate ?? null,
          })),
        })),
      })),
    }));
  },

  /**
   * Update email configuration for a club
   * @param clubShortName Club short name
   * @param emailConfig Email configuration data
   * @returns Promise with result
   */
  async updateEmailConfiguration(
    clubId: number,
    emailConfig: EmailConfigInput,
  ): Promise<{ success: boolean; message?: string }> {
    const data = runGraphQL(
      await apolloClient.mutate({
        mutation: UpdateEmailConfigurationDocument,
        variables: { clubId, emailConfig },
      }),
      { fallbackMessage: "Failed to update email configuration." },
    );

    return {
      success: data.updateEmailConfiguration?.success ?? false,
      message: data.updateEmailConfiguration?.message ?? undefined,
    };
  },

  /**
   * Test email configuration for a club
   * @param clubShortName Club short name
   * @param emailConfig Email configuration data
   * @returns Promise with test result
   */
  async testEmailConfiguration(
    clubId: number,
    emailConfig: EmailConfigInput,
  ): Promise<{ success: boolean; message?: string }> {
    const data = runGraphQL(
      await apolloClient.mutate({
        mutation: TestEmailConfigurationDocument,
        variables: { clubId, emailConfig },
      }),
      { fallbackMessage: "Failed to test email configuration." },
    );

    return {
      success: data.testEmailConfiguration?.success ?? false,
      message: data.testEmailConfiguration?.message ?? undefined,
    };
  },

  /**
   * Update which classes of automated notification copy the club inbox (issue #1960).
   *
   * `prefs` is a patch, not the whole block: the resolver writes the toggles it is given and defaults the rest
   * from the record its compare-and-swap commits against, so an omitted toggle keeps whatever another officer
   * saved since this page read the club (#3842). Pass only the toggles that moved.
   *
   * @param clubId Club ID
   * @param prefs The notification preference toggles that changed
   * @returns Promise with result
   */
  async updateNotificationPreferences(
    clubId: number,
    prefs: Partial<ClubNotificationPreferences>,
  ): Promise<{ success: boolean; message?: string }> {
    const data = runGraphQL(
      await apolloClient.mutate({
        mutation: UpdateNotificationPreferencesDocument,
        variables: {
          clubId,
          ...prefs,
        },
      }),
      { fallbackMessage: "Failed to update notification preferences." },
    );

    return {
      success: data.updateNotificationPreferences?.success ?? false,
      message: data.updateNotificationPreferences?.message ?? undefined,
    };
  },

  /**
   * The EasySpeak instances a club mapping may name, with the name to show a person (#4732).
   *
   * Through `runGraphQL` rather than reading `data` off the result, which is the whole point of
   * putting it here: the shared client sets `errorPolicy: "all"`, so a resolver or authorization
   * failure resolves with `errors` and null `data` instead of rejecting. Read directly, such a
   * failure is indistinguishable from a deployment that configured no instances -- and the caller
   * would render an empty dropdown that can never save a mapping, with nothing saying to retry.
   */
  async getEasySpeakInstances(): Promise<EasySpeakInstanceOption[]> {
    const data = runGraphQL(
      await apolloClient.query<GetEasySpeakInstancesQuery>({
        query: GetEasySpeakInstancesDocument,
        ...VERSIONED_READ_POLICY,
      }),
      { fallbackMessage: "Failed to load the Easy-Speak instances." },
    );

    return data.easySpeakInstances;
  },
};
